import { useState, useEffect, useRef } from "react";

const DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHS_FULL = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

// view: "days" | "months" | "years"
function DatePicker({ value, onChange, placeholder }) {
    const today = new Date();
    const [open, setOpen] = useState(false);
    const [view, setView] = useState("days");
    const [viewYear, setViewYear] = useState(today.getFullYear());
    const [viewMonth, setViewMonth] = useState(today.getMonth());
    const [yearRangeStart, setYearRangeStart] = useState(today.getFullYear() - 7);
    const ref = useRef(null);

    useEffect(() => {
        function handleClickOutside(e) {
            if (ref.current && !ref.current.contains(e.target)) {
                setOpen(false);
                setView("days");
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const selectedDate = value ? new Date(value) : null;

    const displayValue = selectedDate
        ? `${String(selectedDate.getDate()).padStart(2, "0")}/${String(selectedDate.getMonth() + 1).padStart(2, "0")}/${selectedDate.getFullYear()}`
        : "";

    function prevMonth() {
        if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
        else setViewMonth(m => m - 1);
    }

    function nextMonth() {
        if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
        else setViewMonth(m => m + 1);
    }

    function goToday() {
        setViewMonth(today.getMonth());
        setViewYear(today.getFullYear());
        setView("days");
    }

    function selectDate(day) {
        const d = new Date(viewYear, viewMonth, day);
        const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        onChange(iso);
        setOpen(false);
        setView("days");
    }

    function selectMonth(mIdx) {
        setViewMonth(mIdx);
        setView("days");
    }

    function selectYear(yr) {
        setViewYear(yr);
        setView("months");
    }

    function getDaysInMonth(year, month) {
        return new Date(year, month + 1, 0).getDate();
    }

    function getFirstDayOfMonth(year, month) {
        return new Date(year, month, 1).getDay();
    }

    function renderCalendarDays() {
        const daysInMonth = getDaysInMonth(viewYear, viewMonth);
        const firstDay = getFirstDayOfMonth(viewYear, viewMonth);
        const daysInPrev = getDaysInMonth(viewYear, viewMonth - 1 < 0 ? 11 : viewMonth - 1);
        const cells = [];

        for (let i = firstDay - 1; i >= 0; i--)
            cells.push({ day: daysInPrev - i, current: false });

        for (let d = 1; d <= daysInMonth; d++)
            cells.push({ day: d, current: true });

        const remaining = 42 - cells.length;
        for (let d = 1; d <= remaining; d++)
            cells.push({ day: d, current: false });

        return cells;
    }

    const isToday = (day, current) =>
        current && day === today.getDate() && viewMonth === today.getMonth() && viewYear === today.getFullYear();

    const isSelected = (day, current) =>
        current && selectedDate &&
        day === selectedDate.getDate() &&
        viewMonth === selectedDate.getMonth() &&
        viewYear === selectedDate.getFullYear();

    const isFutureDay = (day, current) => {
        if (!current) return false;
        const d = new Date(viewYear, viewMonth, day);
        const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        return d > todayStart;
    };

    const isFutureMonth = (mIdx) => {
        if (viewYear > today.getFullYear()) return true;
        if (viewYear === today.getFullYear()) return mIdx > today.getMonth();
        return false;
    };

    const isFutureYear = (yr) => yr > today.getFullYear();

    const isAtCurrentMonth = viewYear === today.getFullYear() && viewMonth === today.getMonth();
    const isAtCurrentYear = viewYear === today.getFullYear();
    const isYearRangeAtFuture = yearRangeStart + 16 > today.getFullYear() + 1;

    // Year grid: 16 years starting from yearRangeStart
    const yearList = Array.from({ length: 16 }, (_, i) => yearRangeStart + i);

    return (
        <div ref={ref} style={{ position: "relative", display: "inline-block", width: "100%" }}>
            <div
                className="form-control"
                style={{ cursor: "pointer", userSelect: "none", color: displayValue ? "#333" : "#aaa", display: "flex", alignItems: "center", justifyContent: "center", minHeight: "42px" }}
                onClick={() => { setOpen(o => !o); setView("days"); }}
            >
                {displayValue || placeholder || "DD/MM/YYYY"}
            </div>

            {open && (
                <div style={{
                    position: "absolute",
                    top: "calc(100% + 4px)",
                    left: 0,
                    zIndex: 9999,
                    background: "#fff",
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                    padding: "12px",
                    minWidth: "264px"
                }}>

                    {/* ── DAY VIEW ── */}
                    {view === "days" && (
                        <>
                            <div style={headerStyle}>
                                <button type="button" onClick={prevMonth} style={navBtnStyle}>‹</button>
                                <div style={{ display: "flex", gap: "4px" }}>
                                    <button type="button" onClick={() => setView("months")} style={headerLabelStyle}>
                                        {MONTHS[viewMonth]}
                                    </button>
                                    <button type="button" onClick={() => setView("years")} style={headerLabelStyle}>
                                        {viewYear}
                                    </button>
                                </div>
                                <div style={{ display: "flex", gap: "2px" }}>
                                    <button type="button" onClick={goToday} style={navBtnStyle} title="Today">•</button>
                                    <button type="button" onClick={nextMonth} style={navBtnStyle} disabled={isAtCurrentMonth}>›</button>
                                </div>
                            </div>

                            <div style={gridStyle7}>
                                {DAYS.map(d => (
                                    <div key={d} style={{ textAlign: "center", fontSize: "12px", fontWeight: 600, color: "#888", padding: "4px 0" }}>
                                        {d}
                                    </div>
                                ))}
                            </div>

                            <div style={gridStyle7}>
                                {renderCalendarDays().map((cell, i) => {
                                    const todayCell = isToday(cell.day, cell.current);
                                    const selectedCell = isSelected(cell.day, cell.current);
                                    const futureCell = isFutureDay(cell.day, cell.current);
                                    const clickable = cell.current && !futureCell;
                                    return (
                                        <div
                                            key={i}
                                            onClick={() => clickable && selectDate(cell.day)}
                                            style={{
                                                textAlign: "center",
                                                fontSize: "13px",
                                                cursor: clickable ? "pointer" : "default",
                                                color: selectedCell ? "#fff" : futureCell ? "#ddd" : todayCell ? "#25826A" : cell.current ? "#333" : "#ccc",
                                                background: selectedCell ? "#25826A" : "transparent",
                                                borderRadius: "50%",
                                                width: "32px",
                                                height: "32px",
                                                margin: "0 auto",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontWeight: todayCell ? 700 : 400,
                                                border: todayCell && !selectedCell ? "2px solid #25826A" : "2px solid transparent",
                                            }}
                                        >
                                            {cell.day}
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}

                    {/* ── MONTH VIEW ── */}
                    {view === "months" && (
                        <>
                            <div style={headerStyle}>
                                <button type="button" onClick={() => setViewYear(y => y - 1)} style={navBtnStyle}>‹</button>
                                <button type="button" onClick={() => setView("years")} style={headerLabelStyle}>
                                    {viewYear}
                                </button>
                                <button type="button" onClick={() => setViewYear(y => y + 1)} style={navBtnStyle} disabled={isAtCurrentYear}>›</button>
                            </div>

                            <div style={gridStyle4}>
                                {MONTHS_FULL.map((m, i) => {
                                    const isCurrentMonth = i === today.getMonth() && viewYear === today.getFullYear();
                                    const isSelMonth = i === viewMonth && selectedDate && viewYear === selectedDate.getFullYear();
                                    const futureMonth = isFutureMonth(i);
                                    return (
                                        <div
                                            key={m}
                                            onClick={() => !futureMonth && selectMonth(i)}
                                            style={{
                                                textAlign: "center",
                                                padding: "8px 4px",
                                                cursor: futureMonth ? "default" : "pointer",
                                                fontSize: "13px",
                                                borderRadius: "6px",
                                                fontWeight: isCurrentMonth ? 700 : 400,
                                                color: isSelMonth ? "#fff" : futureMonth ? "#ddd" : isCurrentMonth ? "#25826A" : "#333",
                                                background: isSelMonth ? "#25826A" : "transparent",
                                                border: isCurrentMonth && !isSelMonth ? "1px solid #25826A" : "1px solid transparent",
                                            }}
                                        >
                                            {m}
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}

                    {/* ── YEAR VIEW ── */}
                    {view === "years" && (
                        <>
                            <div style={headerStyle}>
                                <button type="button" onClick={() => setYearRangeStart(y => y - 16)} style={navBtnStyle}>‹</button>
                                <span style={{ fontWeight: 700, fontSize: "14px", color: "#003a37" }}>
                                    {yearRangeStart} – {yearRangeStart + 15}
                                </span>
                                <button type="button" onClick={() => setYearRangeStart(y => y + 16)} style={navBtnStyle} disabled={isYearRangeAtFuture}>›</button>
                            </div>

                            <div style={gridStyle4}>
                                {yearList.map(yr => {
                                    const isCurYear = yr === today.getFullYear();
                                    const isSelYear = yr === viewYear && selectedDate && yr === selectedDate.getFullYear();
                                    const futureYear = isFutureYear(yr);
                                    return (
                                        <div
                                            key={yr}
                                            onClick={() => !futureYear && selectYear(yr)}
                                            style={{
                                                textAlign: "center",
                                                padding: "8px 4px",
                                                cursor: futureYear ? "default" : "pointer",
                                                fontSize: "13px",
                                                borderRadius: "6px",
                                                fontWeight: isCurYear ? 700 : 400,
                                                color: isSelYear ? "#fff" : futureYear ? "#ddd" : isCurYear ? "#25826A" : "#333",
                                                background: isSelYear ? "#25826A" : "transparent",
                                                border: isCurYear && !isSelYear ? "1px solid #25826A" : "1px solid transparent",
                                            }}
                                        >
                                            {yr}
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}

                </div>
            )}
        </div>
    );
}

const navBtnStyle = {
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: "18px",
    color: "#333",
    padding: "0 6px",
    lineHeight: 1,
};

const headerLabelStyle = {
    background: "none",
    border: "none",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: "15px",
    color: "#003a37",
    padding: "0 4px",
};

const headerStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "10px",
};

const gridStyle7 = {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: "2px",
};

const gridStyle4 = {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "4px",
    marginTop: "4px",
};

export default DatePicker;
